import React, { useState } from 'react';
import { Box, Button, Divider, HStack, Link, Text, Tooltip, VStack } from '@chakra-ui/react';
import { MoonStarIcon, SparklesIcon, ListChecksIcon, BookOpenIcon, ImagesIcon, QuoteIcon, PhoneIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { brand, images, whatsappHref } from '../data/brand';
const navItems = [{
  label: 'Servicios',
  href: '#servicios',
  icon: SparklesIcon
}, {
  label: '¿Por qué consultarnos?',
  href: '#por-que',
  icon: ListChecksIcon
}, {
  label: 'Acerca de nosotros',
  href: '#nosotros',
  icon: BookOpenIcon
}, {
  label: 'Galería y equipo',
  href: '#galeria',
  icon: ImagesIcon
}, {
  label: 'Experiencias',
  href: '#experiencias',
  icon: QuoteIcon
}];
function Ripple({
  id
}: {
  id: number;
}) {
  return <Box key={id} position="absolute" inset={0} borderRadius="full" bg="gold.400" pointerEvents="none" animation="goldRipple 0.6s ease-out forwards" data-id="element-173" data-mp="3q7blkx7csf11" />;
}
export function SideNav() {
  const [ripples, setRipples] = useState<Record<string, number>>({});
  const fireRipple = (key: string) => {
    setRipples(current => ({
      ...current,
      [key]: Date.now()
    }));
  };
  return <>
      {/* Desktop: floating vertical capsule anchored to the right edge */}
      <VStack as="nav" aria-label="Navegación principal" display={{
      base: 'none',
      lg: 'flex'
    }} position="fixed" right={6} top="50%" transform="translateY(-50%)" zIndex={40} spacing={3} px={2.5} py={4} borderRadius="full" bg="rgba(13,13,13,0.72)" backdropFilter="blur(14px)" border="1px solid" borderColor="rgba(242,183,5,0.35)" boxShadow="0 18px 46px rgba(0,0,0,0.55)" data-id="element-174" data-mp="wygviwk0fjqr">
        <Tooltip label="Templo de Ayuda Esotérica" placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-175" data-mp="23n74iuzqzwrh">
          <Link href="#inicio" aria-label="Ir al inicio" position="relative" overflow="hidden" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="crimson.500" color="cream.50" onClick={() => fireRipple('logo')} _hover={{
          bg: 'crimson.400'
        }} data-id="element-176" data-mp="28f74uojqcrol">
            <MoonStarIcon size={20} aria-hidden="true" data-id="element-177" data-mp="1zbtd2chw62xf" />
            {ripples.logo && <Ripple id={ripples.logo} data-id="element-178" data-mp="1ukl5zn41atnf" />}
          </Link>
        </Tooltip>

        <Divider borderColor="rgba(242,183,5,0.3)" data-id="element-179" data-mp="1uj9zhfj7f7sm" />

        {navItems.map(item => <Tooltip key={item.href} label={item.label} placement="left" hasArrow bg="ink.700" color="gold.300" openDelay={80} data-id="element-180" data-mp="1qj7zdat4amfg">
            <Link href={item.href} aria-label={item.label} position="relative" overflow="hidden" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" color="cream.100" border="1px solid" borderColor="transparent" onClick={() => fireRipple(item.href)} transition="all 0.22s ease" _hover={{
          color: 'gold.300',
          borderColor: 'rgba(242,183,5,0.5)',
          bg: 'rgba(242,183,5,0.1)'
        }} data-id="element-181" data-mp="1gmmuqql0fdc5">
              <item.icon size={19} aria-hidden="true" data-id="element-182" data-mp="spqg58lnwkd8" />
              {ripples[item.href] && <Ripple id={ripples[item.href]} data-id="element-183" data-mp="snm8cfkn717o" />}
            </Link>
          </Tooltip>)}

        <Divider borderColor="rgba(242,183,5,0.3)" data-id="element-184" data-mp="xi080u6c7ui5" />

        <Tooltip label={`Llamar ${brand.phone}`} placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-185" data-mp="2f0nxzkhn">
          <Link href={brand.phoneHref} aria-label={`Llamar al ${brand.phone}`} display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="gold.400" color="ink.900" _hover={{
          bg: 'gold.300'
        }} data-id="element-186" data-mp="4ybd1dm29z0x">
            <PhoneIcon size={19} aria-hidden="true" data-id="element-187" data-mp="2bn3ji80ra77c" />
          </Link>
        </Tooltip>

        <Tooltip label="Escribir por WhatsApp" placement="left" hasArrow bg="ink.700" color="gold.300" data-id="element-188" data-mp="2pervmbb08vob">
          <Link href={whatsappHref()} isExternal aria-label="Escribir por WhatsApp" display="flex" alignItems="center" justifyContent="center" w="2.75rem" h="2.75rem" borderRadius="full" bg="wa.500" color="ink.900" _hover={{
          bg: 'wa.600'
        }} data-id="element-189" data-mp="2lcwkyg76p5xv">
            <WhatsAppIcon size={21} data-id="element-190" data-mp="101n1u7bjm0v8" />
          </Link>
        </Tooltip>
      </VStack>

      {/* Mobile / tablet: fixed top bar with the two real contact actions */}
      <Box position="fixed" top={0} left={0} right={0} zIndex={40} display={{
      base: 'block',
      lg: 'none'
    }} backgroundImage={`url(${images.heartsHead})`} backgroundSize="cover" backgroundPosition="center" borderBottom="1px solid" borderColor="rgba(242,183,5,0.35)" px={3} py={3} data-id="element-191" data-mp="3gn1rzn6tuu23">
        <Box position="absolute" inset={0} bg="linear-gradient(90deg, rgba(13,13,13,0.94) 0%, rgba(13,13,13,0.78) 50%, rgba(196,18,48,0.45) 100%)" aria-hidden="true" data-id="element-192" data-mp="2sztmb45hlizj" />
        <HStack spacing={3} maxW="30rem" mx="auto" position="relative" data-id="element-193" data-mp="2ny4avnecr3ax">
          <Button as="a" href={brand.phoneHref} flex="1" h="3.25rem" fontSize="xs" bg="#1d74e8" color="white" _hover={{
          bg: '#1866cc',
          textDecoration: 'none'
        }} _active={{
          bg: '#15589f'
        }} sx={{
          animation: 'pulseGlowBlue 2.1s ease-in-out infinite'
        }} leftIcon={<PhoneIcon size={17} aria-hidden="true" data-id="element-195" data-mp="fpfy3zumj6cj" />} data-id="element-194" data-mp="2o0gu03vujjpk">
            <VStack spacing={0} align="flex-start" data-id="element-196" data-mp="aspjzi2dqktr">
              <Text as="span" fontSize="0.7rem" letterSpacing="0.14em" data-id="element-197" data-mp="athf6rs2egji">
                LLAMAR
              </Text>
              <Text as="span" fontSize="0.62rem" fontWeight={500} letterSpacing="0.02em" data-id="element-198" data-mp="13g27pvka0yon">
                954-674-1691
              </Text>
            </VStack>
          </Button>
          <Button as="a" href={whatsappHref()} target="_blank" rel="noopener noreferrer" variant="whatsapp" flex="1" h="3.25rem" fontSize="xs" sx={{
          animation: 'pulseGlowGreen 2.1s ease-in-out infinite 0.35s'
        }} leftIcon={<WhatsAppIcon size={19} data-id="element-200" data-mp="1hfl3dovwihk2" />} data-id="element-199" data-mp="yjl7i2zsytv7">
            <VStack spacing={0} align="flex-start" data-id="element-201" data-mp="1hetjvcfkwaa5">
              <Text as="span" fontSize="0.7rem" letterSpacing="0.14em" data-id="element-202" data-mp="303eaijwykyze">
                WHATSAPP
              </Text>
              <Text as="span" fontSize="0.62rem" fontWeight={500} letterSpacing="0.02em" data-id="element-203" data-mp="352z5kj90cn6u">
                954-658-7182
              </Text>
            </VStack>
          </Button>
        </HStack>
      </Box>
    </>;
}